import { arabicLabels, englishLabels, Icon, ThemeProvider } from '@lubehub/ui-web';
import { useSyncExternalStore } from 'react';
import { NavLink, Outlet } from 'react-router';

import { t } from '../copy';
import { PUBLIC_PATHS } from '../legal/identity';
import { usePublicLocale } from '../locale';

const LINKS = [
  { to: PUBLIC_PATHS.home, key: 'nav.home' },
  { to: PUBLIC_PATHS.privacy, key: 'nav.privacy' },
  { to: PUBLIC_PATHS.terms, key: 'nav.terms' },
  { to: PUBLIC_PATHS.support, key: 'nav.support' },
  { to: PUBLIC_PATHS.accountDeletion, key: 'nav.deletion' },
] as const;

const DARK_QUERY = '(prefers-color-scheme: dark)';

function subscribeToScheme(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener('change', onChange);

  return () => {
    media.removeEventListener('change', onChange);
  };
}

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_QUERY).matches
  );
}

/**
 * The public site follows the visitor's own light or dark preference — the
 * site has no account to store a choice in, and a visitor who reads in the
 * dark should not be handed a white page.
 */
function useSystemTheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribeToScheme, prefersDark, () => false) ? 'dark' : 'light';
}

export function PublicShell() {
  const { locale, setLocale, dir } = usePublicLocale();
  const theme = useSystemTheme();

  return (
    <ThemeProvider
      name={theme}
      direction={dir}
      labels={locale === 'en' ? englishLabels : arabicLabels}
    >
      <div className="public-shell">
        <header className="public-header">
          <NavLink to={PUBLIC_PATHS.home} className="public-brand" data-testid="brand">
            <span className="public-brand__mark" aria-hidden="true">
              <Icon name="droplet" size={20} />
            </span>
            <span className="public-brand__names">
              <span className="public-brand__en">CarCarNa</span>
              <span className="public-brand__ar">الكار كارنا</span>
            </span>
          </NavLink>
          <nav className="public-nav" aria-label={t(locale, 'nav.aria')}>
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  ['public-nav__link', isActive ? 'public-nav__link--active' : '']
                    .filter(Boolean)
                    .join(' ')
                }
                end={link.to === PUBLIC_PATHS.home}
              >
                {t(locale, link.key)}
              </NavLink>
            ))}
          </nav>
          <div className="public-lang" role="group" aria-label={t(locale, 'nav.lang.label')}>
            <button
              type="button"
              className={
                locale === 'ar' ? 'public-lang__btn public-lang__btn--active' : 'public-lang__btn'
              }
              aria-pressed={locale === 'ar'}
              onClick={() => {
                setLocale('ar');
              }}
              data-testid="locale-ar"
            >
              {t(locale, 'nav.lang.ar')}
            </button>
            <button
              type="button"
              className={
                locale === 'en' ? 'public-lang__btn public-lang__btn--active' : 'public-lang__btn'
              }
              aria-pressed={locale === 'en'}
              onClick={() => {
                setLocale('en');
              }}
              data-testid="locale-en"
            >
              {t(locale, 'nav.lang.en')}
            </button>
          </div>
        </header>
        <main className="public-main">
          <Outlet />
        </main>
        <footer className="public-footer">
          <p>{t(locale, 'footer.product')}</p>
          <p>{t(locale, 'footer.operator')}</p>
        </footer>
      </div>
    </ThemeProvider>
  );
}
